import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaEye, FaEyeSlash, FaSpinner, FaPencilAlt, FaTrash } from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import { SINGAPORE_BANKS } from '../../utils/constants';
import './Providers.css';

const EditProvider = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        address: '',
        city: '',
        zipCode: '',
        businessName: '',
        uen: '',
        categoryId: '',
        bio: '',
        contactPerson: '',
        contactInfo: '',
        unitNumber: '',
        bankAccountHolderName: '',
        bankName: '',
        bankAccountNumber: '',
        bankRoutingNumber: '',
        referralCode: '',
        password: '',
        hourlyRate: 0,
        loyaltyPoints: 0,
        loyaltyTier: 'bronze',
        isHighRisk: false,
        isAvailable: true,
        internalNotes: '',
        avatar: '',
        idDocumentUrl: '',
        certificationDocumentUrl: '',
        insuranceDocumentUrl: '',

        // Service Details
        aboutMe: '',
        languagesSpoken: '',
        yearsOfExperience: '',
        ageGroupsComfortableWith: [],
        availableForUrgentBooking: false,
        availableOnWeekends: false,
        comfortableWithPets: false,
        preferredServiceLocation: '',
        nationality: '',
        availableFrom: '',
        confinementServicesProvided: [],
        cuisinesExperiencedIn: [],
        certificationOrQualification: '',
        consultationTypesOffered: [],
        availableForUrgentConsultation: false,
        age: '',
        roomPreference: [],
        monthlyRate: '',
        extraChargesInfo: '',
        serviceAreaIds: []
    });

    const [serviceAreas, setServiceAreas] = useState([]);
    const [newAreaName, setNewAreaName] = useState('');
    const [isAddingArea, setIsAddingArea] = useState(false);

    const ENUMS = {
        PreferredServiceLocation: ['Family Home', 'Own Home', 'Accompany Me'],
        RoomPreference: ['Shared Room', 'Single Room'],
        ConfinementService: [
            'Baby Care', 'Night Care', 'Cooking Confinement Meals',
            'Traditional Confinement Meals Cooking',
            'Light Household Chores', 'Breastfeeding Support', 'Herbal Baths/Tonics'
        ],
        Cuisine: ['Chinese', 'Malay', 'Indian', 'Fusion / Modern'],
        ConsultationType: ['Home Visit', 'Virtual Consultation', 'Clinic Visit'],
        AgeGroup: ['Infant', 'Toddler', 'Preschool', 'Older Kids']
    };

    const [uploading, setUploading] = useState({
        id: false,
        cert: false,
        insur: false
    });

    useEffect(() => {
        fetchCategories();
        fetchServiceAreas();
        fetchProvider();
    }, [id]);

    const fetchServiceAreas = async () => {
        try {
            const response = await api.get(ENDPOINTS.SERVICE_AREAS.base);
            setServiceAreas(response.data || []);
        } catch (error) {
            console.error('Failed to fetch service areas:', error);
        }
    };

    const handleAddArea = async () => {
        if (!newAreaName.trim()) {
            toast.warning('Please enter an area name');
            return;
        }

        setIsAddingArea(true);
        try {
            const response = await api.post(ENDPOINTS.SERVICE_AREAS.base, { name: newAreaName });
            const newArea = response.data;

            toast.success(`Area "${newArea.name}" added!`);

            // Refresh list
            await fetchServiceAreas();

            // Auto-select
            setFormData(prev => ({
                ...prev,
                serviceAreaIds: [...(prev.serviceAreaIds || []), newArea.id]
            }));

            setNewAreaName('');
        } catch (error) {
            console.error('Failed to add service area:', error);
            toast.error('Failed to add area: ' + (error.response?.data?.message || error.message));
        } finally {
            setIsAddingArea(false);
        }
    };

    const handleEditArea = async (id, currentName) => {
        const newName = window.prompt(`Rename service area "${currentName}" to:`, currentName);
        if (!newName || newName === currentName) return;

        try {
            await api.patch(`${ENDPOINTS.SERVICE_AREAS.base}/${id}`, { name: newName });
            toast.success('Area updated');
            fetchServiceAreas();
        } catch (error) {
            toast.error('Update failed');
        }
    };

    const handleDeleteArea = async (id, name) => {
        if (!window.confirm(`Are you sure you want to delete "${name}"? This will remove it globally.`)) return;

        try {
            await api.delete(`${ENDPOINTS.SERVICE_AREAS.base}/${id}`);
            toast.success('Area deleted');

            // Remove from selection
            setFormData(prev => ({
                ...prev,
                serviceAreaIds: (prev.serviceAreaIds || []).filter(areaId => areaId !== id)
            }));

            fetchServiceAreas();
        } catch (error) {
            toast.error('Delete failed');
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await api.get(ENDPOINTS.SERVICES.CATEGORIES);
            setCategories(response.data || []);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
            toast.error('Failed to load categories');
        }
    };

    const fetchProvider = async () => {
        try {
            const response = await api.get(`${ENDPOINTS.PROVIDERS.base}/${id}`);
            const provider = response.data || response;

            // Determine categoryId: prioritize provider.categoryId, fallback to first service's category
            let categoryId = provider.categoryId || '';

            if (!categoryId && provider.services && provider.services.length > 0) {
                const service = provider.services[0];
                categoryId = typeof service.category === 'object' ? service.category.id : (service.categoryId || '');
            }

            // Swap detection/fallback for legacy swapped database entries
            let loadedBankName = provider.bankName || '';
            let loadedBankRoutingNumber = provider.bankRoutingNumber || '';

            const isRoutingBank = SINGAPORE_BANKS.includes(provider.bankRoutingNumber);
            const isNameBank = SINGAPORE_BANKS.includes(provider.bankName);

            if (isRoutingBank && !isNameBank) {
                // Legacy data swap detected: Bank Name was stored in routing number
                loadedBankName = provider.bankRoutingNumber;
                loadedBankRoutingNumber = provider.bankName;
            }

            setFormData({
                email: provider.email || '',
                firstName: provider.firstName || '',
                lastName: provider.lastName || '',
                phone: provider.phone || '',
                address: provider.address || '',
                city: provider.city || '',
                zipCode: provider.zipCode || '',
                businessName: provider.businessName || '',
                uen: provider.uen || '',
                categoryId: categoryId,
                bio: provider.bio || '',
                contactPerson: provider.contactPerson || '',
                contactInfo: provider.contactInfo || '',
                unitNumber: provider.unitNumber || '',
                bankAccountHolderName: provider.bankAccountHolderName || '',
                bankName: loadedBankName,
                bankAccountNumber: provider.bankAccountNumber || '',
                bankRoutingNumber: loadedBankRoutingNumber,
                referralCode: provider.referralCode || '',
                password: '',
                hourlyRate: provider.hourlyRate || 0,
                loyaltyPoints: provider.loyaltyPoints || 0,
                loyaltyTier: provider.loyaltyTier || 'bronze',
                isHighRisk: !!provider.isHighRisk,
                isAvailable: provider.isAvailable !== false,
                internalNotes: provider.internalNotes || '',
                avatar: provider.avatar || '',
                idDocumentUrl: provider.idDocumentUrl || '',
                certificationDocumentUrl: provider.certificationDocumentUrl || '',
                insuranceDocumentUrl: provider.insuranceDocumentUrl || '',

                // Service Details (Grouped)
                aboutMe: provider.serviceDetails?.aboutMe || '',
                languagesSpoken: provider.serviceDetails?.languagesSpoken || '',
                yearsOfExperience: provider.serviceDetails?.yearsOfExperience || '',
                ageGroupsComfortableWith: provider.serviceDetails?.ageGroupsComfortableWith || [],
                availableForUrgentBooking: !!provider.serviceDetails?.availableForUrgentBooking,
                availableOnWeekends: !!provider.serviceDetails?.availableOnWeekends,
                comfortableWithPets: !!provider.serviceDetails?.comfortableWithPets,
                preferredServiceLocation: Array.isArray(provider.serviceDetails?.preferredServiceLocation)
                    ? provider.serviceDetails.preferredServiceLocation
                    : (provider.serviceDetails?.preferredServiceLocation ? [provider.serviceDetails.preferredServiceLocation] : []),
                nationality: provider.serviceDetails?.nationality || '',
                availableFrom: provider.serviceDetails?.availableFrom ? provider.serviceDetails.availableFrom.split('T')[0] : '',
                confinementServicesProvided: provider.serviceDetails?.confinementServicesProvided || [],
                cuisinesExperiencedIn: provider.serviceDetails?.cuisinesExperiencedIn || [],
                certificationOrQualification: provider.serviceDetails?.certificationOrQualification || '',
                consultationTypesOffered: provider.serviceDetails?.consultationTypesOffered || [],
                availableForUrgentConsultation: !!provider.serviceDetails?.availableForUrgentConsultation,
                age: provider.serviceDetails?.age || '',
                roomPreference: provider.serviceDetails?.roomPreference || [],
                monthlyRate: provider.serviceDetails?.monthlyRate || '',
                extraChargesInfo: provider.serviceDetails?.extraChargesInfo || '',
                serviceAreaIds: provider.serviceDetails?.serviceAreas?.map(a => a.id) || []
            });
        } catch (error) {
            console.error('Failed to fetch provider:', error);
            toast.error('Failed to load provider details');
            navigate('/providers');
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.firstName) newErrors.firstName = 'First Name is required';
        if (!formData.lastName) newErrors.lastName = 'Last Name is required';
        if (!formData.phone) {
            newErrors.phone = 'Phone is required';
        } else if (!/^\+[0-9]{10}$/.test(formData.phone)) {
            newErrors.phone = 'Phone number must be exactly 11 characters (+ followed by 10 digits)';
        }
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Service Details validation
        if (formData.categoryId) {
            if (!formData.availableFrom) {
                newErrors.availableFrom = 'Available From Date is required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }
        setSubmitting(true);
        try {
            const {
                aboutMe, languagesSpoken, yearsOfExperience, ageGroupsComfortableWith,
                availableForUrgentBooking, availableOnWeekends, comfortableWithPets,
                preferredServiceLocation, nationality, availableFrom,
                confinementServicesProvided, cuisinesExperiencedIn,
                certificationOrQualification, consultationTypesOffered,
                availableForUrgentConsultation,
                age, roomPreference, monthlyRate, extraChargesInfo,
                serviceAreaIds,
                ...basicInfo
            } = formData;

            const payload = {
                ...basicInfo,
                categoryId: formData.categoryId ? parseInt(formData.categoryId) : undefined,
                serviceAreaIds, // Root level so backend update() picks it up directly
                serviceDetails: {
                    aboutMe: aboutMe || '',
                    languagesSpoken,
                    yearsOfExperience: yearsOfExperience ? parseInt(yearsOfExperience) : undefined,
                    ageGroupsComfortableWith,
                    availableForUrgentBooking,
                    availableOnWeekends,
                    comfortableWithPets,
                    preferredServiceLocation,
                    nationality,
                    availableFrom,
                    confinementServicesProvided,
                    cuisinesExperiencedIn,
                    certificationOrQualification,
                    consultationTypesOffered,
                    availableForUrgentConsultation,
                    age: age ? parseInt(age) : undefined,
                    roomPreference,
                    monthlyRate: (monthlyRate !== '' && monthlyRate !== null && monthlyRate !== undefined) ? parseFloat(monthlyRate) : undefined,
                    extraChargesInfo,
                }
            };

            if (!payload.password) delete payload.password;

            // Numeric conversions
            payload.hourlyRate = parseFloat(payload.hourlyRate) || 0;
            payload.loyaltyPoints = parseInt(payload.loyaltyPoints) || 0;

            await api.patch(`${ENDPOINTS.PROVIDERS.base}/${id}`, payload);
            toast.success('Provider updated successfully!');

            // Immediate reset and navigation
            setSubmitting(false);
            setTimeout(() => {
                navigate(`/providers/${id}`);
            }, 500);
        } catch (error) {
            console.error('Failed to update provider:', error);
            toast.error('Failed to update provider: ' + (error.response?.data?.message || error.message));
            setSubmitting(false);
        }
    };

    const handleFileUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(prev => ({ ...prev, [type]: true }));

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await api.post(`${ENDPOINTS.PROVIDERS.base.replace('/providers', '')}/media/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            const url = response.url || response.data?.url;

            if (type === 'id') setFormData(prev => ({ ...prev, idDocumentUrl: url }));
            if (type === 'cert') setFormData(prev => ({ ...prev, certificationDocumentUrl: url }));
            if (type === 'insur') setFormData(prev => ({ ...prev, insuranceDocumentUrl: url }));

            toast.success('File uploaded successfully!');
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error('File upload failed');
        } finally {
            setUploading(prev => ({ ...prev, [type]: false }));
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Restrict numeric fields
        // Restrict phone field to numbers and + only
        if (name === 'phone') {
            const cleanedValue = value.replace(/[^\d+]/g, '');
            const finalValue = cleanedValue.startsWith('+')
                ? '+' + cleanedValue.slice(1).replace(/\+/g, '')
                : cleanedValue.replace(/\+/g, '');

            setFormData(prev => ({
                ...prev,
                [name]: finalValue.slice(0, 15)
            }));
            return;
        }

        // Restrict bank account number to numbers only
        if (name === 'bankAccountNumber') {
            const numericValue = value.replace(/\D/g, '');
            setFormData(prev => ({
                ...prev,
                [name]: numericValue
            }));
            return;
        }

        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleMultiSelect = (name, value) => {
        setFormData(prev => {
            const current = prev[name] || [];
            if (current.includes(value)) {
                return { ...prev, [name]: current.filter(item => item !== value) };
            } else {
                return { ...prev, [name]: [...current, value] };
            }
        });
    };

    const renderServiceDetails = () => {
        const catId = Number(formData.categoryId);
        if (!catId) return null;

        return (
            <div className="service-details-section full-width mt-4" style={{ gridColumn: 'span 1 / -1' }}>
                <h3 className="section-subtitle">Bio & Service Details</h3>

                {/* aboutMe - All categories */}
                <div className="form-group full-width">
                    <label>Biography / About Me *</label>
                    <textarea
                        name="aboutMe"
                        value={formData.aboutMe}
                        onChange={handleChange}
                        className="form-control"
                        rows="4"
                        placeholder="Introduce yourself and your experience..."
                        required
                    />
                </div>


                {/* Category Specific Fields */}
                <div className="form-grid-2 mt-3">
                    {/* Languages Spoken (5, 8, 19, 10) */}
                    {[5, 8, 19, 10].includes(catId) && (
                        <div className="form-group">
                            <label>Languages Spoken *</label>
                            <input type="text" name="languagesSpoken" value={formData.languagesSpoken} onChange={handleChange} className="form-control" placeholder="e.g. English, Mandarin" required />
                        </div>
                    )}

                    {/* Years of Experience (5, 8) */}
                    {[5, 8].includes(catId) && (
                        <div className="form-group">
                            <label>Years of Experience *</label>
                            <input type="number" name="yearsOfExperience" value={formData.yearsOfExperience} onChange={handleChange} className="form-control" required />
                        </div>
                    )}

                    {/* Nationality (8) */}
                    {catId === 8 && (
                        <div className="form-group">
                            <label>Nationality *</label>
                            <input type="text" name="nationality" value={formData.nationality} onChange={handleChange} className="form-control" required />
                        </div>
                    )}

                    {/* Age (8) */}
                    {catId === 8 && (
                        <div className="form-group">
                            <label>Age *</label>
                            <input type="number" name="age" value={formData.age} onChange={handleChange} className="form-control" required />
                        </div>
                    )}

                    {/* Available From - Visible for all categories */}
                    <div className="form-group">
                        <label>Available From *</label>
                        <input type="date" name="availableFrom" value={formData.availableFrom} onChange={handleChange} className={`form-control ${errors.availableFrom ? 'is-invalid' : ''}`} required />
                        {errors.availableFrom && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.availableFrom}</span>}
                    </div>

                    {/* Certification (19, 10) */}
                    {[19, 10].includes(catId) && (
                        <div className="form-group">
                            <label>Certification / Qualification *</label>
                            <input type="text" name="certificationOrQualification" value={formData.certificationOrQualification} onChange={handleChange} className="form-control" required />
                        </div>
                    )}
                </div>

                {/* Multi-selects and Toggles */}
                <div className="form-grid-2 mt-3">
                    {/* Age Groups Comfortable With (5) */}
                    {catId === 5 && (
                        <div className="form-group">
                            <label>Age Groups Comfortable With</label>
                            <div className="checkbox-cloud">
                                {ENUMS.AgeGroup.map(age => (
                                    <label key={age} className={`cloud-item ${formData.ageGroupsComfortableWith?.includes(age) ? 'active' : ''}`}>
                                        <input type="checkbox" checked={formData.ageGroupsComfortableWith?.includes(age)} onChange={() => handleMultiSelect('ageGroupsComfortableWith', age)} />
                                        {age}
                                    </label>

                                ))}
                            </div>
                        </div>
                    )}

                    {/* Confinement Services Provided (8) */}
                    {catId === 8 && (
                        <div className="form-group">
                            <label>Confinement Services Provided</label>
                            <div className="checkbox-cloud">
                                {ENUMS.ConfinementService.map(service => (
                                    <label key={service} className={`cloud-item ${formData.confinementServicesProvided?.includes(service) ? 'active' : ''}`}>
                                        <input type="checkbox" checked={formData.confinementServicesProvided?.includes(service)} onChange={() => handleMultiSelect('confinementServicesProvided', service)} />
                                        {service}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Cuisines (8) */}
                    {catId === 8 && (
                        <div className="form-group">
                            <label>Cuisines Experienced In</label>
                            <div className="checkbox-cloud">
                                {ENUMS.Cuisine.map(cuisine => (
                                    <label key={cuisine} className={`cloud-item ${formData.cuisinesExperiencedIn?.includes(cuisine) ? 'active' : ''}`}>
                                        <input type="checkbox" checked={formData.cuisinesExperiencedIn?.includes(cuisine)} onChange={() => handleMultiSelect('cuisinesExperiencedIn', cuisine)} />
                                        {cuisine}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Consultation Types (10, 9) */}
                    {[10, 9].includes(catId) && (
                        <div className="form-group">
                            <label>Consultation Types Offered</label>
                            <div className="checkbox-cloud">
                                {ENUMS.ConsultationType.filter(type => catId === 9 ? type !== 'Virtual Consultation' : true).map(type => (
                                    <label key={type} className={`cloud-item ${formData.consultationTypesOffered?.includes(type) ? 'active' : ''}`}>
                                        <input type="checkbox" checked={formData.consultationTypesOffered?.includes(type)} onChange={() => handleMultiSelect('consultationTypesOffered', type)} />
                                        {type}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Preferences & Availability Section */}
                <h5 className="section-subtitle mt-4">Preferences & Availability</h5>

                <div className="form-grid-2 mt-3">
                    {/* Babysitter Location (Multi-select) */}
                    {catId === 5 && (
                        <div className="form-group">
                            <label>Preferred Service Location</label>
                            <div className="checkbox-cloud">
                                {ENUMS.PreferredServiceLocation.map(loc => (
                                    <label key={loc} className={`cloud-item ${formData.preferredServiceLocation?.includes(loc) ? 'active' : ''}`}>
                                        <input type="checkbox" checked={formData.preferredServiceLocation?.includes(loc)} onChange={() => handleMultiSelect('preferredServiceLocation', loc)} />
                                        {loc}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Nanny Room Preference (Multi-select) */}
                    {catId === 8 && (
                        <div className="form-group">
                            <label>Room Preference</label>
                            <div className="checkbox-cloud">
                                {ENUMS.RoomPreference.map(room => (
                                    <label key={room} className={`cloud-item ${formData.roomPreference?.includes(room) ? 'active' : ''}`}>
                                        <input type="checkbox" checked={formData.roomPreference?.includes(room)} onChange={() => handleMultiSelect('roomPreference', room)} />
                                        {room}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Nanny Pricing & Additional Info (8) */}
                {catId === 8 && (
                    <div className="form-grid-2 mt-3">
                        <div className="form-group">
                            <label>Monthly Rate (30 Days) $</label>
                            <input type="number" name="monthlyRate" value={formData.monthlyRate} onChange={handleChange} className="form-control" placeholder="e.g. 3800" />
                        </div>
                        <div className="form-group">
                            <label>Additional Charges (Twins, Multiples, Extra Duties)</label>
                            <textarea
                                name="extraChargesInfo"
                                value={formData.extraChargesInfo}
                                onChange={handleChange}
                                className="form-control"
                                rows="2"
                                placeholder="e.g. Extra $200 for twins..."
                            />
                        </div>
                    </div>
                )}

                {/* Dropdowns for Availability & Preferences */}
                <div className="form-grid-2 mt-3">
                    {/* Babysitter Settings (5) */}
                    {catId === 5 && (
                        <>
                            <div className="form-group">
                                <label>Available for Urgent Booking</label>
                                <select name="availableForUrgentBooking" value={formData.availableForUrgentBooking ? 'true' : 'false'} onChange={(e) => setFormData(prev => ({ ...prev, availableForUrgentBooking: e.target.value === 'true' }))} className="form-control">
                                    <option value="true">YES</option>
                                    <option value="false">NO</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Available on Weekends</label>
                                <select name="availableOnWeekends" value={formData.availableOnWeekends ? 'true' : 'false'} onChange={(e) => setFormData(prev => ({ ...prev, availableOnWeekends: e.target.value === 'true' }))} className="form-control">
                                    <option value="true">YES</option>
                                    <option value="false">NO</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Comfortable with Pets</label>
                                <select name="comfortableWithPets" value={formData.comfortableWithPets ? 'true' : 'false'} onChange={(e) => setFormData(prev => ({ ...prev, comfortableWithPets: e.target.value === 'true' }))} className="form-control">
                                    <option value="true">YES</option>
                                    <option value="false">NO</option>
                                </select>
                            </div>
                        </>
                    )}

                    {/* Nanny Settings (8) */}
                    {catId === 8 && (
                        <>
                            <div className="form-group">
                                <label>Comfortable with Pets</label>
                                <select name="comfortableWithPets" value={formData.comfortableWithPets ? 'true' : 'false'} onChange={(e) => setFormData(prev => ({ ...prev, comfortableWithPets: e.target.value === 'true' }))} className="form-control">
                                    <option value="true">YES</option>
                                    <option value="false">NO</option>
                                </select>
                            </div>
                        </>
                    )}

                    {/* Professional Settings (10, 9) */}
                    {[10, 9].includes(catId) && (
                        <>
                            <div className="form-group">
                                <label>Urgent / Same Day Consultation</label>
                                <select name="availableForUrgentConsultation" value={formData.availableForUrgentConsultation ? 'true' : 'false'} onChange={(e) => setFormData(prev => ({ ...prev, availableForUrgentConsultation: e.target.value === 'true' }))} className="form-control">
                                    <option value="true">YES</option>
                                    <option value="false">NO</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Available on Weekends</label>
                                <select name="availableOnWeekends" value={formData.availableOnWeekends ? 'true' : 'false'} onChange={(e) => setFormData(prev => ({ ...prev, availableOnWeekends: e.target.value === 'true' }))} className="form-control">
                                    <option value="true">YES</option>
                                    <option value="false">NO</option>
                                </select>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    if (loading) return (
        <div className="loading-state" style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div className="spinner"></div>
            <p style={{ marginTop: '20px', color: '#636e72', fontWeight: '500' }}>Loading Provider Details...</p>
        </div>
    );

    return (
        <div className="create-provider-page edit-provider-page">
            <div className="page-header">
                <div className="header-left">
                    <button onClick={() => navigate(`/providers/${id}`)} className="btn-back">
                        <FaArrowLeft /> Back to Details
                    </button>
                    <h1>Edit Provider</h1>
                </div>
            </div>

            <div className="edit-grid">
                <form onSubmit={handleSubmit} className="provider-form form-grid">
                    {/* Basic Info Section */}
                    <div className="content-card">
                        <h3 className="card-title">Personal Information</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label>First Name *</label>
                                <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="form-control" required disabled />
                            </div>
                            <div className="form-group">
                                <label>Last Name *</label>
                                <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="form-control" required disabled />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Email *</label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} className="form-control" required disabled />
                            </div>
                            <div className="form-group">
                                <label>Phone *</label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                    placeholder="e.g. +6581234567"
                                    maxLength="11"
                                    required
                                />
                                {errors.phone && <span className="error-message" style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px', display: 'block' }}>{errors.phone}</span>}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>New Password (leave blank to keep current)</label>
                            <div className="password-input-wrapper" style={{ position: 'relative' }}>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="Enter new password"
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none' }}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Business/Professional Section */}
                    <div className="content-card">
                        <h3 className="card-title">Professional Details</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Business Name</label>
                                <input type="text" name="businessName" value={formData.businessName} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="form-group">
                                <label>Hourly Rate ($)</label>
                                <input type="number" step="0.01" name="hourlyRate" value={formData.hourlyRate} onChange={handleChange} className="form-control" />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Category <span className="text-danger">*</span></label>
                                <select
                                    name="categoryId"
                                    value={formData.categoryId}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                >
                                    <option value="">-- Choose Primary Category --</option>
                                    {categories.map((category) => (
                                        <option key={category.id} value={category.id}>{category.name}</option>
                                    ))}
                                </select>
                            </div>
                            {/* <div className="form-group checkbox-group" style={{ display: 'flex', alignItems: 'center', marginTop: '25px' }}>
                                <label className="checkbox-label" style={{ fontWeight: 600 }}>
                                    <input type="checkbox" name="isAvailable" checked={formData.isAvailable} onChange={handleChange} />
                                    Active & Available for Bookings
                                </label>
                            </div> */}
                        </div>

                        {/* Service Areas - Always Visible */}
                        <div className="form-group full-width mb-4 mt-2">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <label style={{ marginBottom: 0, fontWeight: '600' }}>Service Areas (Select all that apply)</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <input
                                        type="text"
                                        placeholder="New area name..."
                                        value={newAreaName}
                                        onChange={(e) => setNewAreaName(e.target.value)}
                                        style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}
                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddArea())}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-secondary"
                                        onClick={handleAddArea}
                                        disabled={isAddingArea}
                                        style={{ padding: '6px 15px' }}
                                    >
                                        {isAddingArea ? 'Adding...' : 'Add Area'}
                                    </button>
                                </div>
                            </div>
                            {serviceAreas.length > 0 ? (
                                <div className="checkbox-cloud">
                                    {serviceAreas.map(area => (
                                        <div key={area.id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <label className={`cloud-item ${formData.serviceAreaIds?.includes(area.id) ? 'active' : ''}`} style={{ marginBottom: 0 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.serviceAreaIds?.includes(area.id)}
                                                    onChange={() => handleMultiSelect('serviceAreaIds', area.id)}
                                                />
                                                {area.name}
                                            </label>
                                            <div style={{ display: 'flex', gap: '4px', marginLeft: '2px' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleEditArea(area.id, area.name)}
                                                    style={{ border: 'none', background: 'none', padding: '4px', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' }}
                                                    title="Edit Area"
                                                >
                                                    <FaPencilAlt size={11} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteArea(area.id, area.name)}
                                                    style={{ border: 'none', background: 'none', padding: '4px', cursor: 'pointer', color: '#ff4d4f', display: 'flex', alignItems: 'center' }}
                                                    title="Delete Area"
                                                >
                                                    <FaTrash size={11} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-2 border rounded bg-light text-muted">
                                    Loading service areas...
                                </div>
                            )}
                        </div>

                        <div className="form-group full-width">
                            <label>Short Bio (Legacy)</label>
                            <textarea name="bio" value={formData.bio} onChange={handleChange} className="form-control" rows="2"></textarea>
                        </div>

                        {renderServiceDetails()}
                    </div>

                    {/* Address Section */}
                    <div className="content-card">
                        <h3 className="card-title">Location & Contact</h3>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label>Address</label>
                                <input type="text" name="address" value={formData.address} onChange={handleChange} className="form-control" />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>City</label>
                                <input type="text" name="city" value={formData.city} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="form-group">
                                <label>Unit Number</label>
                                <input type="text" name="unitNumber" value={formData.unitNumber} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="form-group">
                                <label>Zip Code</label>
                                <input type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} className="form-control" />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>UEN / Registration</label>
                                <input type="text" name="uen" value={formData.uen} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="form-group">
                                <label>Referral Code (Own)</label>
                                <input type="text" name="referralCode" value={formData.referralCode} onChange={handleChange} className="form-control" readOnly />
                            </div>
                        </div>
                    </div>

                    {/* Bank & Financial Section */}
                    <div className="content-card">
                        <h3 className="card-title">Banking & Financials</h3>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label>Account Holder Name *</label>
                                <input type="text" name="bankAccountHolderName" value={formData.bankAccountHolderName} onChange={handleChange} className="form-control" required />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group full-width">
                                <label>Bank Name *</label>
                                <select
                                    name="bankName"
                                    value={formData.bankName}
                                    onChange={handleChange}
                                    className="form-control"
                                    required
                                >
                                    <option value="">-- Select Bank --</option>
                                    {SINGAPORE_BANKS.map(bank => (
                                        <option key={bank} value={bank}>{bank}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Account Number *</label>
                                <input
                                    type="text"
                                    name="bankAccountNumber"
                                    value={formData.bankAccountNumber}
                                    onChange={handleChange}
                                    className="form-control"
                                    pattern="\d*"
                                    placeholder="Numbers only"
                                    required
                                />
                            </div>
                            <div className="form-group" style={{ display: 'none' }}>
                                <label>Routing / Branch Code</label>
                                <input type="text" name="bankRoutingNumber" value={formData.bankRoutingNumber} onChange={handleChange} className="form-control" />
                            </div>
                        </div>
                    </div>

                    {/* Admin/Stats Section */}
                    <div className="content-card">
                        <h3 className="card-title">Administrative Settings</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Loyalty Points</label>
                                <input type="number" name="loyaltyPoints" value={formData.loyaltyPoints} onChange={handleChange} className="form-control" />
                            </div>
                            <div className="form-group">
                                <label>Loyalty Tier</label>
                                <select name="loyaltyTier" value={formData.loyaltyTier} onChange={handleChange} className="form-control">
                                    <option value="bronze">Bronze</option>
                                    <option value="silver">Silver</option>
                                    <option value="gold">Gold</option>
                                    <option value="platinum">Platinum</option>
                                </select>
                            </div>
                        </div>
                        {/* <div className="form-row" style={{ marginTop: '15px' }}>
                            <div className="form-group checkbox-group">
                                <label className="checkbox-label" style={{ color: '#e53e3e', fontWeight: 700 }}>
                                    <input type="checkbox" name="isHighRisk" checked={formData.isHighRisk} onChange={handleChange} />
                                    Mark as High Risk Account
                                </label>
                            </div>
                        </div> */}
                        <div className="form-group full-width" style={{ marginTop: '10px' }}>
                            <label>Internal Admin Notes</label>
                            <textarea name="internalNotes" value={formData.internalNotes} onChange={handleChange} className="form-control" rows="3" placeholder="Private notes for team only..."></textarea>
                        </div>
                    </div>

                    {/* Verification Documents Section */}
                    <div className="content-card">
                        <h3 className="card-title">Verification Documents</h3>
                        {/* <div className="form-group">
                            <label>ID Document (NRIC/Passport) *</label>
                            <div className="file-upload-container">
                                <input 
                                    type="file" 
                                    onChange={(e) => handleFileUpload(e, 'id')} 
                                    className="form-control"
                                    accept="image/*,.pdf"
                                />
                                {uploading.id && <span className="upload-loader">Uploading...</span>}
                                {formData.idDocumentUrl && (
                                    <div className="upload-success">
                                        ✅ Uploaded | <a href={formData.idDocumentUrl} target="_blank" rel="noopener noreferrer">View Current</a>
                                    </div>
                                )}
                            </div>
                        </div> */}
                        <div className="form-group">
                            <label>Certification (Optional)</label>
                            <div className="file-upload-container">
                                <input
                                    type="file"
                                    onChange={(e) => handleFileUpload(e, 'cert')}
                                    className="form-control"
                                    accept="image/*,.pdf"
                                />
                                {uploading.cert && <span className="upload-loader">Uploading...</span>}
                                {formData.certificationDocumentUrl && (
                                    <div className="upload-success">
                                        ✅ Uploaded | <a href={formData.certificationDocumentUrl} target="_blank" rel="noopener noreferrer">View Current</a>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Insurance Document (Optional)</label>
                            <div className="file-upload-container">
                                <input
                                    type="file"
                                    onChange={(e) => handleFileUpload(e, 'insur')}
                                    className="form-control"
                                    accept="image/*,.pdf"
                                />
                                {uploading.insur && <span className="upload-loader">Uploading...</span>}
                                {formData.insuranceDocumentUrl && (
                                    <div className="upload-success">
                                        ✅ Uploaded | <a href={formData.insuranceDocumentUrl} target="_blank" rel="noopener noreferrer">View Current</a>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="form-actions" style={{ gridColumn: 'span 1 / -1', marginTop: '20px' }}>
                        <button
                            type="submit"
                            className="btn btn-primary btn-save"
                            disabled={submitting}
                            style={{ width: '100%', padding: '15px', fontSize: '1.1rem', opacity: submitting ? 0.7 : 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}
                        >
                            {submitting ? (
                                <><FaSpinner className="fa-spin" /> Updating...</>
                            ) : (
                                <><FaSave /> Update Provider Profile</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditProvider;
